// ============================================================================
// JARVISTRAVEL - LAST-KNOWN TRIPS (JAR-2266)
// The trips a signed-in person's Dashboard last showed, kept for the page
// session. The Dashboard remounts on every return from the trip map or a
// journal story, and each mount used to start from nothing: the new-user
// welcome, then a trip of defaults, then the real trip. It now paints these
// first and refreshes them quietly. Memory only, never storage, and purged
// with the rest of a person's data on sign-out.
// ============================================================================

import type { JournalTrip } from '@/app/types';
import type { TripsOwner } from './localTrips';

type Known = { list: JournalTrip[] | null; trips: Map<string, JournalTrip> };

const known = new Map<TripsOwner, Known>();

function entry(owner: TripsOwner): Known {
  let e = known.get(owner);
  if (!e) known.set(owner, (e = { list: null, trips: new Map() }));
  return e;
}

/** Core's trip list as it last answered for this person, or null if it hasn't. */
export const lastKnownList = (owner: TripsOwner): JournalTrip[] | null => known.get(owner)?.list ?? null;

/** A trip as this person last saw it in full, from a detail read or their own edits. */
export const lastKnownTrip = (owner: TripsOwner, id: string): JournalTrip | undefined => known.get(owner)?.trips.get(id);

export function rememberList(owner: TripsOwner, list: JournalTrip[]): void {
  entry(owner).list = list;
}

export function rememberTrip(owner: TripsOwner, trip: JournalTrip): void {
  entry(owner).trips.set(trip.id, trip);
}

/** Everyone's, on sign-out, and between tests. */
export function forgetLastKnownTrips(): void {
  known.clear();
}
